import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_URLS } from '../config/api';

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: ForgotPasswordSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError('');
      try {
        const response = await axios.post(
          API_URLS.FORGOT_PASSWORD,
          {
            email: values.email
          }
        );
        
        console.log('Forgot password response:', response.data);
        setIsSubmitted(true);
      } catch (error) {
        console.error('Forgot password error:', error);
        const serverData = error?.response?.data;
        let extractedMessage =
          (typeof serverData?.message === 'string' && serverData.message) ||
          (typeof serverData?.error === 'string' && serverData.error) ||
          (serverData?.error && typeof serverData.error?.message === 'string' && serverData.error.message) ||
          (typeof error?.message === 'string' && error.message) ||
          '';

        if (!extractedMessage || typeof extractedMessage !== 'string') {
          extractedMessage = 'Failed to send reset link. Please try again.';
        }
        setError(extractedMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleResendEmail = () => {
    setIsSubmitted(false);
    setError('');
    formik.resetForm();
  };

  if (isSubmitted) {
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/loginBackgroundImage.png)`,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Success Message Overlay */}
        <div className="absolute left-0 top-0 w-full md:w-1/2 lg:w-1/2 h-full flex items-center justify-center">
          <div className="max-w-sm w-full space-y-8 mx-8">
            {/* Sparks Logo */}
            <div className="text-center mb-8">
              <img 
                src={`${process.env.PUBLIC_URL}/sparks_logo_file.svg`} 
                alt="Sparks Logo" 
                className="h-16 w-auto mx-auto mb-6"
              />
            </div>
            
            <div className="text-center mb-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-green-600 mb-4">
                Check Your Email
              </h2>
              <p className="text-gray-600 mb-6">
                We've sent a password reset link to <strong>{formik.values.email}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Please check your email and follow the instructions to reset your password. 
                If you don't see the email, check your spam folder.
              </p>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={handleResendEmail}
                className="w-full py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 transition-colors"
              >
                Resend Email
              </button>
              
              <button
                onClick={handleBackToLogin}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/loginBackgroundImage.png)`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Forgot Password Form Overlay */}
      <div className="absolute left-0 top-0 w-full md:w-1/2 lg:w-1/2 h-full flex items-center justify-center">
        <div className="max-w-sm w-full space-y-8 mx-8">
          {/* Sparks Logo */}
          <div className="text-center mb-8">
            <img 
              src={`${process.env.PUBLIC_URL}/sparks_logo_file.svg`} 
              alt="Sparks Logo" 
              className="h-16 w-auto mx-auto mb-6"
            />
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-pink-600 mb-2">
              Forgot Password?
            </h2>
            <p className="text-gray-600 mb-8">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
          
          <form className="space-y-6" onSubmit={formik.handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  formik.touched.email && formik.errors.email
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : ''
                }`}
                placeholder="Enter your email address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.email}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading || !formik.isValid}
                className="w-full py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending Reset Link...
                  </div>
                ) : (
                  'SEND RESET LINK'
                )}
              </button>
              
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors flex items-center justify-center"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
